import { defineStore } from 'pinia';
import { useStoreRoot } from '../abstract/root';
import { useStorePaymentsNotification } from '../services/paymentsNotification';
import { useStorePaymentSystemImages } from '../strapi/paymentSystemImages';
import { useStorePaymentSystemSorting } from '../strapi/paymentSystemSorting';
import { softswiss } from '../transport/softswiss';
import { useStoreSsPlayer } from './player';
import type { PaymentMethodFiatExtendedInterface } from '~/types/paymentMethodFiatExtended.interface';
import type { tUsaPaymentMethods } from '~/types/paymentMethods';
import type { Arguments, LibError, Payment, Results } from '~/types/paymentsApi';
import type { IStrapiImage } from '~/types/strapiImage';

export const useStoreSsPaymentMethods = defineStore('ssPaymentMethods', {
  state: () => ({
    methods: [] as Results.GetMethodsResult,
    selectedMethod: null as Payment.Method | null,
    paymentPlan: null as Results.GetMethodFieldsResult | null,
    paymentError: {} as LibError.SubmitError,
    savePlayerFieldsError: {} as LibError.SavePlayerFieldsError,
    isPaymentLibLoaded: false,
    currencyLoadedMethods: '',
  }),

  getters: {
    ssPaymentMethods(): PaymentMethodFiatExtendedInterface[] {
      const sortingGroup = useStorePaymentSystemSorting().depositSortingData.find((sorting) =>
        useStoreSsPlayer().playerGroupIds.includes(sorting.groupId)
      );

      return this.methods
        .map((method) => {
          const { brand, provider } = method;
          const currency = method.termsOfService.restrictions.amountCurrencyCode;
          const position = sortingGroup?.paymentSystems.find((system) => {
            const methodId = (
              provider === 'coinspaid' ? `${brand}_${currency}` : brand
            )?.toLowerCase();
            return system.paymentSystemId === methodId;
          })?.position;

          return {
            ...method,
            position: position || 99,
            methodLogoId: (provider === 'coinspaid'
              ? `${brand}_${currency}`
              : brand
            )?.toLowerCase(),
          };
        })
        .sort((a, b) => a.position - b.position);
    },

    selectedMethodExtended(): PaymentMethodFiatExtendedInterface | null {
      if (this.selectedMethod === null) {
        return null;
      }

      return this.ssPaymentMethods.find((method) => method.id === this.selectedMethod?.id) || null;
    },

    selectedMethodCode(): string {
      return this.selectedMethod?.termsOfService.restrictions.amountCurrencyCode || '';
    },

    selectedMethodSavedData(): any[] | null {
      return this.selectedMethodExtended?.savedProfiles || null;
    },

    paymentPlanFields(): string[] {
      const fields: string[] = [];

      this.paymentPlan?.methodFields.forEach((item) => {
        if (item.fieldName) {
          fields.push(item.fieldName);
        }
      });

      return fields;
    },

    /**
     * Getter that returns if payment fields are form saved method (cvv and amount only)
     * or stadart (with encCreditcardNumber etc)
     * @returns boolean
     */
    isFieldsFromSavedMethod(): boolean {
      return !this.paymentPlanFields?.includes('encCreditcardNumber');
    },
    selectedMethodType(): string {
      return this.selectedMethod?.type || '';
    },
    usaPaymentMethods(): {
      id: tUsaPaymentMethods;
      payment_provider_logo: IStrapiImage;
      methodLogoId: string;
      isUsaMethod: boolean;
    }[] {
      const storePaymentSystemImages = useStorePaymentSystemImages();
      const visaIcon =
        storePaymentSystemImages.data.find((el) => el.methodLogoId === 'buycrypto_usa_creditcard')
          ?.paymentProviderLogo || {};
      const paypalIcon =
        storePaymentSystemImages.data.find((el) => el.methodLogoId === 'buycrypto_usa_payPal')
          ?.paymentProviderLogo || {};
      return [
        {
          id: 'buycrypto_usa_creditcard',
          payment_provider_logo: visaIcon,
          methodLogoId: 'buycrypto_creditcard',
          isUsaMethod: true,
        },
        {
          id: 'buycrypto_usa_payPal',
          payment_provider_logo: paypalIcon,
          methodLogoId: 'buycrypto_usa_payPal',
          isUsaMethod: true,
        },
      ];
    },
  },

  actions: {
    async loadConfig(currency: string, methodId: string) {
      const ssPaymentsApi = window.PaymentsAPI;
      const domain = window.location.host;

      const root = useStoreRoot();

      const data = await ssPaymentsApi?.config({
        language: root.userLang,
        redirectRoutes: {
          successUrl: `https://${domain}/deposit/${currency}/status/success/method/${methodId}`,
          failureUrl: `https://${domain}/deposit/${currency}/status/fail/method/${methodId}`,
        },
        cdnList: ['https://cdn2.softswiss.net', 'https://cdn.softswiss.net/'],
      });

      return data;
    },

    /**
     * Method for loading payments data for selected currency.
     * Payments data were filtering by availability and saved payments methods were adding
     * before saving.
     * @param currency currency for which payment's methods load
     */
    async loadMethods(currency: string): Promise<any> {
      if (this.currencyLoadedMethods === currency) {
        return;
      }

      this.methods = [];

      if (!import.meta.browser || !window) {
        return;
      }

      const ssPaymentsApi = window.PaymentsAPI;

      try {
        const currencyLoad = useStoreSsPlayer().playerCurrency || 'EUR';

        this.loadConfig(currencyLoad, '');

        const methods = await ssPaymentsApi?.getMethods({
          currency,
          paymentAction: 'deposit' as Payment.Action.Deposit,
        });

        if (!methods) {
          return;
        }

        this.methods = methods;
        this.currencyLoadedMethods = currency;
      } catch (e: any) {
        console.error(e);
      }
    },

    /**
     * Method for loading data for submitting deposit with selected payment method
     * @param currency currency for which payment's methods load
     */
    async loadPaymentPlan(currency: string, methodID: string) {
      if (!import.meta.browser || !window) {
        return;
      }

      this.paymentPlan = null;

      const ssPaymentsApi = window.PaymentsAPI;

      try {
        const paymentPlan = await ssPaymentsApi?.getMethodFields({
          currency,
          id: methodID,
          paymentAction: 'deposit' as Payment.Action.Deposit,
        });

        if (!paymentPlan) {
          return;
        }

        this.paymentPlan = paymentPlan;
      } catch (e: any) {
        console.error(e);
      }
    },

    /**
     * Method for loading data for submitting deposit that used
     * if user want to use saved payment method
     * @param params savedMethod - player's saved payment's data
     * @param params currency
     */
    async loadPaymentPlanSavedMethod(params: {
      savedMethod: { id: string };
      currency: string;
      methodID: string;
    }) {
      if (!import.meta.browser || !window) {
        return;
      }

      const ssPaymentsApi = window.PaymentsAPI;

      try {
        const paymentPlan = await ssPaymentsApi?.getMethodFields({
          currency: params.currency,
          id: params.methodID,
          paymentAction: 'deposit' as Payment.Action.Deposit,
          savedProfileId: params.savedMethod.id,
        });

        if (!paymentPlan) {
          return;
        }

        this.paymentPlan = paymentPlan;
      } catch (e: any) {
        console.error(e);
      }
    },

    /**
       * Method tha submit player's inputed payments data
       * @param ctx context
       * @param params player's fields e.g.: {
       *       amount: '20',
               account: 'hans@magnetics.io',
            }
      * @returns DepositResultType if success; DepositError object if payment failed
      */
    async submitDeposit(
      params: Partial<any>,
      playerFieldsData?: Record<string, string>
    ): Promise<any | any | void> {
      if (!import.meta.browser || !window?.location) {
        return;
      }

      try {
        const ssPaymentsApi = window.PaymentsAPI;

        const currency = useStoreSsPlayer().playerCurrency || 'EUR';
        const methodId = this.selectedMethod?.id.replaceAll('-', '_') || '';

        await this.loadConfig(currency, methodId);

        if (this.paymentPlan === null) {
          return;
        }

        const submitData: Arguments.SubmitArgs = {
          amountValue: params.amount,
          currency,
          paymentAction: 'deposit' as Payment.Action,
          id: params.methodId || this.selectedMethod?.id || '',
          methodFieldsData: this.paymentPlan.methodFields
            .filter((item) => item.type !== 'banner')
            .reduce((result, item) => {
              if (item?.fieldName) {
                result[item.fieldName] = params[item.fieldName];
              }
              return result;
            }, {} as Record<string, string>),
        };

        if (playerFieldsData) {
          submitData.playerFieldsData = playerFieldsData;
        }

        if (params.savedProfileId) {
          submitData.savedProfileId = params.savedProfileId;
        }

        const submit = await ssPaymentsApi?.submit(submitData);

        // Disconnect from sockets if the payment was started
        // TODO: add edge-case when user closes iframe modal - handle it properly, right now the sockets are disconnected if user closed modal
        useStorePaymentsNotification().unsubscribe();

        const devcodePopup = document.getElementById('devcode_popup');
        const button = devcodePopup?.getElementsByClassName('close')[0];
        button?.addEventListener('click', (event) => event.stopPropagation());

        return submit;
      } catch (error) {
        this.paymentError = error as any;
        return error as any;
      }
    },

    async savePlayerFields(params: Arguments.SavePlayerFieldsArgs) {
      const data = await window.PaymentsAPI?.savePlayerFields(params);

      return data;
    },

    async deleteSavedProfile(id: string) {
      const currency = useStoreSsPlayer().playerCurrency || 'EUR';

      const data = await window.PaymentsAPI?.deleteSavedProfile({
        currency,
        paymentAction: 'deposit' as Payment.Action,
        id: this.selectedMethod?.id || '',
        savedProfileId: id,
      });

      return data;
    },

    setSelectedMethod(index: number) {
      this.selectedMethod = this.ssPaymentMethods[index];
    },

    resetSelectedMethod() {
      this.selectedMethod = null;
    },

    resetPaymentPlan() {
      this.paymentPlan = null;
    },

    resetErrorState() {
      this.paymentError = {} as LibError.SubmitError;
    },

    async setBonusCode(bonusCode: string): Promise<void> {
      try {
        await softswiss().patch({
          path: `/api/player/set_bonus_code`,
          force: true,
          payload: { deposit_bonus_code: bonusCode },
        });
        await useStoreSsPlayer().load();
      } catch (e: any) {
        console.error(e);
      }
    },

    async deleteBonusCode(): Promise<void> {
      try {
        await softswiss().delete({
          path: `/api/player/clear_bonus_code`,
          force: true,
        });
        await useStoreSsPlayer().load();
      } catch (e: any) {
        console.info(e);
      }
    },

    async updateBonusSettings(canIssue: boolean): Promise<void> {
      try {
        await softswiss().patch({
          path: `/api/player/update_bonus_settings`,
          force: true,
          payload: { can_issue: canIssue },
        });
        await useStoreSsPlayer().load();
      } catch (e: any) {
        console.error(e);
      }
    },

    resetCache() {
      const ssPaymentsApi = window.PaymentsAPI;

      return ssPaymentsApi?.resetCache();
    },
    async loadPaymentLib() {
      if (!this.isPaymentLibLoaded) {
        await scriptLoader('https://payments-lib.cdn.s7s.ai/v1/index.js', 'payment-lib');
        this.isPaymentLibLoaded = true;
      }
    },
    updatePlayerStoragePresets(amount: number, minAmountOption: number) {
      if (amount <= minAmountOption) {
        return;
      }
      const storeSsPlayer = useStoreSsPlayer();
      const storagePlayerPreset = localStorage.getItem(
        `savedPreset_${storeSsPlayer.playerId}_${
          this.selectedMethod!.id
        }_${storeSsPlayer.playerCurrency!}`
      );
      if (storagePlayerPreset && Number(storagePlayerPreset) > amount) {
        return;
      }
      localStorage.setItem(
        `savedPreset_${storeSsPlayer.playerId}_${
          this.selectedMethod!.id
        }_${storeSsPlayer.playerCurrency!}`,
        amount.toString()
      );
    },
    getPlayerStoragePresets() {
      const storeSsPlayer = useStoreSsPlayer();
      return localStorage.getItem(
        `savedPreset_${storeSsPlayer.playerId}_${
          this.selectedMethod!.id
        }_${storeSsPlayer.playerCurrency!}`
      );
    },
  },
});
